import { useEffect, useMemo, useRef, useState } from "react";
import {
  FileText,
  Gavel,
  Headphones,
  Mic,
  Search,
  X
} from "lucide-react";
import type { AppView } from "../app/ui-types";

type CommandPaletteProps = {
  open: boolean;
  onClose: () => void;
  onNavigate: (view: AppView) => void;
};

const COMMANDS: Array<{
  label: string;
  detail: string;
  view: AppView;
  icon: typeof Search;
}> = [
  { label: "Open matter setup", detail: "Authority, people and glossary", view: "matter", icon: Gavel },
  { label: "Start live capture", detail: "Microphone and evidence buffer", view: "live", icon: Mic },
  { label: "Review transcript", detail: "Confidence queue and redlines", view: "review", icon: Headphones },
  { label: "Produce PDF", detail: "Freeze, export and deliver", view: "export", icon: FileText }
];

export function CommandPalette({ open, onClose, onNavigate }: CommandPaletteProps) {
  const dialog = useRef<HTMLDialogElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(0);

  const matches = useMemo(
    () =>
      COMMANDS.filter((command) =>
        `${command.label} ${command.detail}`.toLowerCase().includes(query.toLowerCase())
      ),
    [query]
  );

  useEffect(() => {
    if (open && !dialog.current?.open) {
      dialog.current?.showModal();
      window.setTimeout(() => input.current?.focus(), 0);
    } else if (!open && dialog.current?.open) {
      dialog.current.close();
    }
  }, [open]);

  useEffect(() => setSelected(0), [query]);

  const activate = (view: AppView) => {
    onNavigate(view);
    setQuery("");
    onClose();
  };

  return (
    <dialog
      className="command-dialog"
      ref={dialog}
      onClose={onClose}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === dialog.current) onClose();
      }}
    >
      <div className="command-field">
        <Search size={18} aria-hidden="true" />
        <label>
          <span className="sr-only">Search matters and actions</span>
          <input
            ref={input}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search matters and actions"
            onKeyDown={(event) => {
              if (event.key === "ArrowDown") {
                event.preventDefault();
                setSelected((current) => Math.min(matches.length - 1, current + 1));
              } else if (event.key === "ArrowUp") {
                event.preventDefault();
                setSelected((current) => Math.max(0, current - 1));
              } else if (event.key === "Enter" && matches[selected]) {
                event.preventDefault();
                activate(matches[selected].view);
              }
            }}
          />
        </label>
        <button className="icon-button" type="button" onClick={onClose} aria-label="Close search">
          <X size={17} aria-hidden="true" />
        </button>
      </div>
      <div className="command-results" role="listbox" aria-label="Commands">
        <p>Workspace</p>
        {matches.map((command, index) => {
          const Icon = command.icon;
          return (
            <button
              type="button"
              role="option"
              aria-selected={index === selected}
              className={index === selected ? "is-active" : ""}
              key={command.label}
              onMouseEnter={() => setSelected(index)}
              onClick={() => activate(command.view)}
            >
              <Icon size={17} aria-hidden="true" />
              <span>
                <strong>{command.label}</strong>
                <small>{command.detail}</small>
              </span>
              <kbd>↵</kbd>
            </button>
          );
        })}
        {!matches.length && <span className="command-empty">No matching matter or action.</span>}
      </div>
      <footer className="command-footer">
        <span>
          <kbd>↑</kbd><kbd>↓</kbd> navigate
        </span>
        <span><kbd>↵</kbd> open</span>
        <span><kbd>esc</kbd> close</span>
      </footer>
    </dialog>
  );
}
